import React, { useEffect, useState } from "react";
import ActionGroup from "../components/ActionGroup";
import EditorialContentSection from "../components/EditorialContentSection";
import FaqList from "../components/FaqList";
import SiteLayout from "../components/SiteLayout";
import {
  consultationOptions,
  greyTransitionFaqs,
  greyTransitionJourneySteps,
  greyTransitionStages,
} from "../siteContent";

const greyTransitionActions = [
  { label: "Book Consultation", to: "/contact", variant: "solid" },
];

const greyTransitionIntroduction = {
  closingVariant: "dark",
  eyebrow: "Introduction",
  feature: {
    title: "This is the beginning",
    text: "Where we remove the harsh regrowth line, create your initial blend, and begin the process of growing your natural colour out in a way that feels soft, natural and manageable.",
  },
  cards: [
    {
      title: "A realistic grow out",
      text: "Growing your colour out fully can take time. This transition is designed to start that journey properly, giving you a soft, blended foundation so your hair feels intentional from the very beginning and helps you move away from constant root touch ups.",
    },
    {
      title: "Carefully planned visits",
      text: "Rather than approaching this appointment by appointment, The Grey Transition is completed across three carefully planned visits, ensuring your hair transitions seamlessly without long gaps or inconsistency.",
    },
    {
      title: "Personalised from the start",
      text: "Each appointment is personalised to you. At the start of the journey we complete your colour analysis, determine which seasonal palette suits you, and recommend home care products to help maintain the health and colour at home.",
    },
  ],
  closing: (
    <>
      <h3>Clear, honest guidance from the start</h3>
      <p>
        My role is to guide and support you through the process in a way that
        feels manageable, intentional, and aligned with you from the very
        beginning.
      </p>
      <p>
        During your consultation, we'll look at where you are now, where you'd
        like to be, and create a clear plan for how to get there without harsh
        lines or unpredictable results. I will always be honest with you.
      </p>
    </>
  ),
  strapline: "Grey Blending | Colour Analysis | Confidence-Led Colour",
  title: "A structured approach to blending your natural grey.",
};

const GreyTransition = () => {
  const [availabilityNoticeOpen, setAvailabilityNoticeOpen] = useState(false);

  const closeAvailabilityNotice = () => {
    setAvailabilityNoticeOpen(false);
  };

  useEffect(() => {
    const availabilityNoticeTimer = window.setTimeout(() => {
      setAvailabilityNoticeOpen(true);
    }, 2000);

    return () => window.clearTimeout(availabilityNoticeTimer);
  }, []);

  useEffect(() => {
    if (!availabilityNoticeOpen) return undefined;

    const originalOverflow = document.body.style.overflow;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setAvailabilityNoticeOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [availabilityNoticeOpen]);

  return (
    <SiteLayout>
      {availabilityNoticeOpen && (
        <div
          aria-labelledby="grey-transition-availability-title"
          aria-modal="true"
          className="grey-transition-availability"
          role="dialog"
        >
          <div className="container grey-transition-availability__content">
            <button
              aria-label="Close availability notice"
              className="grey-transition-availability__close"
              onClick={closeAvailabilityNotice}
              type="button"
            >
              <span />
              <span />
            </button>

            <p className="section-eyebrow">The Grey Transition</p>
            <h2 id="grey-transition-availability-title">
              Bespoke multi appointment package
            </h2>
            <p className="grey-transition-availability__status">
              Currently booking through Spring 2027.
            </p>
            <div className="grey-transition-availability__copy">
              <p>
                Due to high demand, availability for my Grey Transition service
                is currently extremely limited and I am not opening appointments
                beyond Spring 2027 at this time.
              </p>
              <p>
                New dates will be released periodically, with priority given to
                clients on my waiting list.
              </p>
              <p className="grey-transition-availability__status" style={{ color: "#f5747a" }}>
                Package pricing is currently under review for future bookings.
              </p>
              <p>Online Consultation required before booking.</p>
            </div>
          </div>
        </div>
      )}

      <section className="page-hero page-hero--grey-transition">
        <div className="container page-hero__grid grey-transition-hero">
          <div className="grey-transition-hero__copy">
            <h1>The Grey Transition</h1>
            <p className="lead">
              A fully guided process designed to remove the harsh regrowth
              line, create your blend, and set your hair up to become soft,
              natural, and low-maintenance.
            </p>
            <ActionGroup
              actions={greyTransitionActions}
              className="grey-transition-hero__actions"
            />
          </div>

          <figure className="grey-transition-hero__image">
            <img
              alt="Soft grey blended hair by Lizzie Fox Colour Studio"
              src="/images/greyBlending-hero1.webp"
            />
          </figure>
        </div>
      </section>

      <section
        aria-labelledby="grey-transition-journey-title"
        className="section grey-journey-section"
      >
        <div className="container grey-journey">
          <div className="grey-journey__heading">
            <h2 id="grey-transition-journey-title">
              The Grey Transition Journey
            </h2>
            <p>
              A considered, confidence-led process designed to help you move
              into your natural grey beautifully, with colour that feels soft,
              personal and completely you.
            </p>
          </div>

          <div className="grey-journey__track" aria-label="The first four steps">
            {greyTransitionJourneySteps.slice(0, 4).map((step) => (
              <article className="grey-journey-step" key={step.title}>
                <span className="grey-journey-step__marker">
                  {step.number}
                </span>
                <div className="grey-journey-card">
                  <h3>{step.title}</h3>
                  <p className="grey-journey-card__subtitle">
                    {step.subtitle}
                  </p>
                  <p>{step.description}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="grey-journey__arrow" aria-hidden="true">
            &darr;
          </div>

          <article className="grey-journey-maintenance">
            <span className="grey-journey-step__marker grey-journey-step__marker--maintenance">
              {greyTransitionJourneySteps[4].number}
            </span>
            <div className="grey-journey-card grey-journey-card--maintenance">
              <h3>{greyTransitionJourneySteps[4].title}</h3>
              <p className="grey-journey-card__subtitle">
                {greyTransitionJourneySteps[4].subtitle}
              </p>
              <p>{greyTransitionJourneySteps[4].description}</p>
            </div>
          </article>

          <ActionGroup
            actions={[
              {
                label: "Begin Your Grey Transition",
                to: "/contact",
                variant: "solid",
              },
            ]}
            className="grey-journey__actions"
          />
        </div>
      </section>

      <EditorialContentSection
        {...greyTransitionIntroduction}
        className="grey-introduction-section"
        id="grey-transition-introduction-title"
      />

      <section className="section consultation-section">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">Consultations</p>
            <h2>Start with a personalised plan</h2>
          </div>

          <div className="consultation-options-grid">
            {consultationOptions.map((option) => (
              <article
                className={`consultation-card consultation-card--${option.badge
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
                key={option.title}
              >
                <div className="consultation-card__header">
                  <p className="consultation-card__badge">{option.badge}</p>
                  <h3>{option.title}</h3>
                </div>

                <p className="consultation-card__description">
                  {option.description}
                </p>

                <div className="consultation-card__includes">
                  <p>Includes</p>
                  <ul>
                    {option.includes.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="consultation-card__investment">
                  <p>Investment</p>
                  <strong>{option.investment.amount}</strong>
                  {option.investment.note && (
                    <span>{option.investment.note}</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--tinted">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">The 3-Stage Journey</p>
            <h2>Your transition appointments</h2>
          </div>

          <div className="stage-grid">
            {greyTransitionStages.map((stage) => (
              <article className="stage-card" key={stage.title}>
                <p className="stage-card__label">{stage.stage}</p>
                <h3>{stage.title}</h3>
                <p>{stage.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section investment-section">
        <div className="container transition-investment">
          <div className="section-heading transition-investment__heading">
            <p className="section-eyebrow">Investment</p>
            <h2>The Grey Transition</h2>
          </div>

          <article className="transition-investment__schedule transition-investment__notice">
            <p className="transition-investment__label">The Grey Transition</p>
            <h3>Bespoke multi appointment package</h3>
            <p className="transition-investment__intro" style={{ color: "#f5747a" }}>
              <strong>Currently booking through Spring 2027.</strong>
            </p>
            <p>
              Due to high demand, availability for my Grey Transition service is
              currently extremely limited and I am not opening appointments
              beyond Spring 2027 at this time.
            </p>
            <p>
              New dates will be released periodically, with priority given to
              clients on my waiting list.
            </p>
            <p className="transition-investment__intro" style={{ color: "#f5747a", fontWeight: "bold" }}>
              Package pricing is currently under review for future bookings.
            </p>
            <p>Online Consultation required before booking.</p>
          </article>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">FAQs</p>
            <h2>Your grey transition, answered</h2>
          </div>
          <FaqList items={greyTransitionFaqs} />
        </div>
      </section>
    </SiteLayout>
  );
};

export default GreyTransition;
